import { ApiError, apiFetch } from "./config.js";
/** How long the answer is polled for before giving up. A deep-research answer (a plan, a search and selection per part, one more
 * round, a challenge pass, the answer) usually takes one to two minutes, at worst about three; the server's retries on a busy model
 * can add more, so ten minutes leaves room. Each poll is a short request, so waiting this long costs nothing on the way. */
const REGULATION_TIMEOUT_MS = 600000;
/** How often the answer is asked for while the job runs. Each poll returns in milliseconds. */
const POLL_MS = 2000;
function wait(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted)
            return reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
        const t = setTimeout(() => {
            signal?.removeEventListener("abort", onAbort);
            resolve();
        }, ms);
        function onAbort() {
            clearTimeout(t);
            reject(signal?.reason ?? new DOMException("Aborted", "AbortError"));
        }
        signal?.addEventListener("abort", onAbort, { once: true });
    });
}
/**
 * Asks HP Genie a question (the whole message is one question, never split) about the indexed regulatory documents (contracts, orders, rules).
 * Every answer comes with the passages it rests on. `history` and `turns` are sent in full so every follow-up is read in the
 * conversation's light until the chat ends. Every question is researched in depth on the server.
 *
 * The question is handed to the server as a job and the answer polled for: no single request stays open for the minutes an answer
 * takes, so a proxy or VPN that drops a quiet connection (one did, at about 90 seconds) cannot lose it.
 */
export async function askRegulation(message, { history = [], turns = [], signal } = {}) {
    const body = { message, history, turns };
    let job;
    try {
        job = await apiFetch("/regulation/ask/jobs", { method: "POST", body, signal });
    }
    catch (error) {
        // An older server without jobs: ask the one-request way.
        if (error instanceof ApiError && (error.status === 404 || error.status === 405)) {
            return apiFetch("/regulation/ask", { method: "POST", body, signal, timeoutMs: REGULATION_TIMEOUT_MS });
        }
        throw error;
    }
    const deadline = Date.now() + REGULATION_TIMEOUT_MS;
    while (job.status === "running") {
        if (Date.now() > deadline)
            throw new Error("The answer is taking longer than expected. Please try again in a minute.");
        await wait(POLL_MS, signal);
        job = await apiFetch(`/regulation/ask/jobs/${job.id}`, { signal });
    }
    if (job.status === "failed" || !job.response)
        throw new Error(job.error || "The answer could not be produced.");
    return job.response;
}
